import { Link } from "react-router";
import { allergyPhrasesForTrip } from "@shared/phrases.ts";
import { chaptersFromDoc, countdownDays, itemsForDate, selectNextAction, stayForDate } from "@shared/itinerary.ts";
import { visibleSuggestions } from "@shared/suggestions.ts";
import { formatLongDate, parseDateOnly, todayInZone, tripPhase } from "@shared/time.ts";
import { chronologicalSchedule, upcomingPayments } from "@shared/upcoming.ts";
import { formatMinor } from "@shared/money.ts";
import { humanStatus } from "@shared/labels.ts";
import { acknowledgeRevision, acknowledgedRevision, readChecklistDone, toggleChecklistDone } from "../../lib/device.ts";
import { fetchLive } from "../../lib/api.ts";
import { weatherLine, type LivePayload } from "../../lib/live.ts";
import { useTrip, useTripClock } from "../../app/trip.tsx";
import { useSession } from "../../app/session.tsx";
import { TimelineItem } from "../../components/TimelineItem.tsx";
import { PlaceActions } from "../../components/PlaceActions.tsx";
import { AddPlanChooser } from "../../components/AddPlanChooser.tsx";
import { Photo } from "../../components/Photo.tsx";
import { useEffect, useState } from "react";
import { AlertSetupCard } from "../updates/AlertSetup.tsx";
import { fetchUpdates } from "../../lib/push.ts";

export function TodayPage() {
  const { trip, loading, devicePreviewNow } = useTrip();
  const { role } = useSession();
  const [live, setLive] = useState<LivePayload | null>(null);
  const [done, setDone] = useState(readChecklistDone);
  const [ack, setAck] = useState(acknowledgedRevision);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const now = useTripClock();

  const phase = trip
    ? tripPhase(now, trip.trip.startDate, trip.trip.endDate, trip.trip.homeTimezone)
    : "before";
  const zone = trip ? (phase === "before" ? trip.trip.homeTimezone : trip.trip.diaryTimezone) : "UTC";
  const date = trip ? todayInZone(now, zone) : "";

  useEffect(() => {
    if (!date) return;
    void fetchLive(date).then((raw) => setLive(raw as LivePayload)).catch(() => setLive(null));
    void fetchUpdates().then((data) => setUnreadAlerts(Number(data.unread ?? 0))).catch(() => setUnreadAlerts(0));
  }, [date]);

  if (!trip && loading) {
    return (
      <main className="page">
        <div className="skeleton" style={{ height: 36, width: "60%", marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 148 }} />
      </main>
    );
  }
  if (!trip) return <main className="page">Today needs the trip document.</main>;

  const action = selectNextAction(trip, now);
  const stay = stayForDate(trip, date);
  const upcomingStay = stay ?? (phase === "during" ? trip.stays.find((s) => s.startDate >= date) : undefined);
  const place = upcomingStay ? trip.places.find((p) => p.id === upcomingStay.placeId) : undefined;
  const chapters = chaptersFromDoc(trip);
  const chapter = chapters.find((c) => c.stay?.id === upcomingStay?.id) ?? chapters[0];
  const items = itemsForDate(trip, date);
  const schedule = chronologicalSchedule(upcomingPayments(trip).filter((item) => !item.paid));
  const dueSoon = [
    ...schedule.dated.filter((item) => item.date && item.date <= date),
    ...schedule.dated.filter((item) => item.date && item.date > date).slice(0, 3),
  ];
  const weather = weatherLine(live?.weather, date, now, zone, phase === "during");
  const weatherNote = live?.weatherNote;
  const prep = trip.preparationItems;
  const unseen = trip.revisions.filter((rev) => rev.version > ack && !rev.summary.toLowerCase().includes("fx"));
  const daysLeft = countdownDays(now, trip.trip.startDate, trip.trip.homeTimezone);
  const milk = allergyPhrasesForTrip(trip.thaiPhrases)[0];
  const pickup = trip.events.find((event) => event.kind === "transfer" && !event.archived && event.date === date);
  const nextDate = date ? parseDateOnly(date).plus({ days: 1 }).toISODate() : null;
  const boat = trip.reviewItems.find((item) => item.id === "boats" && !item.dismissed);
  const nextPay = schedule.dated[0];
  const suggestions = visibleSuggestions(trip, role);

  return (
    <main className={`page today-page today-${phase}`}>
      {devicePreviewNow && role === "owner" ? (
        <p className="banner warn">Preview clock on this phone only: {devicePreviewNow}</p>
      ) : null}
      <section className={`hero ${phase === "before" ? "today-hero" : "today-hero compact"}`}>
        <Photo
          assetId={chapter?.imageAssetId ?? "journey"}
          alt={`${chapter?.name ?? "Thailand"} destination photograph`}
          priority
          height={phase === "before" ? 220 : 160}
        />
        <div className="hero-copy">
          {phase === "before" ? (
            <>
              <p className="kicker">Before we go</p>
              <p className="today-count tabular">{daysLeft}</p>
              <h1 className="page-title today-heading">
                {daysLeft === 1 ? "day until we leave" : "days until we leave"}
              </h1>
            </>
          ) : phase === "after" ? (
            <>
              <p className="kicker">After the trip</p>
              <h1 className="page-title today-heading">The record</h1>
            </>
          ) : (
            <>
              <p className="kicker">{place?.name ?? "Today"}</p>
              <h1 className="page-title today-heading">{formatLongDate(date, zone)}</h1>
            </>
          )}
        </div>
      </section>
      {weather ? (
        <p className="today-weather">
          {phase === "before" && (place?.name ?? chapter?.name)
            ? `${place?.name ?? chapter?.name} today · ${weather.text}`
            : weather.text}
          {weather.stale ? " · cached" : ""}
        </p>
      ) : weatherNote ? (
        <p className="today-weather muted">{weatherNote}</p>
      ) : null}

      <AlertSetupCard />

      {unreadAlerts > 0 ? (
        <section className="banner">
          <p className="kicker">Updates</p>
          <p>{unreadAlerts === 1 ? "1 actionable update" : `${unreadAlerts} actionable updates`} in the inbox.</p>
          <Link className="btn" to="/updates">Open Updates</Link>
        </section>
      ) : (
        <p><Link className="btn-ghost" to="/updates">Updates inbox</Link></p>
      )}

      {unseen.length ? (
        <section className="banner">
          <p className="kicker">What changed</p>
          {unseen.slice(-3).map((rev) => (
            <p key={rev.version}>{rev.summary}</p>
          ))}
          <button className="btn-ghost" type="button" onClick={() => { acknowledgeRevision(trip.revision); setAck(trip.revision); }}>
            Got it
          </button>
        </section>
      ) : null}

      {phase === "before" ? (
        <section className="coming-up">
          <h2 className="section-title">Coming up</h2>
          {nextPay ? (
            <Link className="coming-row" to={`/money/${nextPay.bookingId}`}>
              <p className="kicker">Next expected payment</p>
              <p className="amount-pair tabular">{formatMinor(nextPay.currency, nextPay.amountMinor)}</p>
              <p>
                {nextPay.householdLabel ?? "Household share"}
                {" · "}
                {nextPay.date ? formatLongDate(nextPay.date) : "Date to confirm"}
                {" · "}
                {humanStatus(nextPay.group)}
              </p>
              <p className="muted">{nextPay.title}</p>
            </Link>
          ) : null}
          {prep[0] ? <p className="coming-row"><span className="kicker">Preparation</span> {prep[0].title}</p> : null}
          {boat ? (
            <Link className="coming-row" to="/essentials/review">
              <p className="kicker">Still unknown</p>
              <p>Boat crossing is not in the van package</p>
            </Link>
          ) : null}
        </section>
      ) : (
        <section className="card today-next">
          <p className="kicker">{action.kicker}{action.when === "later" ? " · later" : ""}</p>
          <h2 className="serif" style={{ margin: "8px 0" }}>{action.title}</h2>
          {action.place ? <p>{action.place}</p> : null}
          {action.note ? <p>{action.note}</p> : null}
          <Link className="btn" to={action.primaryHref}>{action.primaryLabel}</Link>
        </section>
      )}

      {phase === "during" ? (
        <nav className="today-shortcuts" aria-label="Today shortcuts">
          <Link className="shortcut" to={`/journey/day/${date}`}>Today’s plan</Link>
          {upcomingStay ? <Link className="shortcut" to={`/journey/stay/${upcomingStay.id}`}>Hotel</Link> : null}
          {pickup ? <Link className="shortcut" to={`/journey/transfer/${pickup.id}`}>Pickup</Link> : (
            place ? <Link className="shortcut" to={`/map?place=${place.id}`}>Directions</Link> : null
          )}
          {milk ? <Link className="shortcut" to={`/show/${milk.id}`}>Milk allergy</Link> : null}
          {nextDate ? <Link className="shortcut" to={`/journey/day/${nextDate}`}>Tomorrow</Link> : null}
        </nav>
      ) : null}

      {phase === "after" ? (
        <p><Link className="btn" to="/money">Open remaining money</Link></p>
      ) : phase === "before" ? null : (
        <>
          <h3 className="section-title">Today’s plan</h3>
          <div className="timeline">
            {items.length === 0 ? <p className="muted">Free time. Tonight’s hotel is below.</p> : null}
            {items.map((item) => <TimelineItem key={item.id} item={item} />)}
          </div>
          {role === "owner" ? (
            <details className="today-add">
              <summary>Add to this day</summary>
              <AddPlanChooser day={date} />
            </details>
          ) : null}
        </>
      )}

      {role !== "owner" && suggestions.length ? (
        <section>
          <h3 className="section-title">Notes from Chris</h3>
          {suggestions.map((s) => (
            <article key={s.id} className="card suggestion-row">
              <p><strong>{s.title}</strong></p>
              <p className="muted">{s.detail}</p>
            </article>
          ))}
        </section>
      ) : null}

      {role === "owner" && prep.length ? (
        <section>
          <h3 className="section-title">Chris’s administrative queue</h3>
          {prep.map((item) => (
            <label key={item.id} className="fact-row" style={{ alignItems: "center" }}>
              <span>
                <input
                  type="checkbox"
                  checked={Boolean(done[item.id])}
                  onChange={() => setDone(toggleChecklistDone(item.id))}
                />{" "}
                {item.title}
                <span className="muted"> · On this device</span>
              </span>
            </label>
          ))}
        </section>
      ) : null}

      {dueSoon.length ? (
        <section>
          <h3 className="section-title">Payments</h3>
          {dueSoon.map((item) => (
            <Link key={`${item.bookingId}-${item.householdId}`} className="pay-row" to={`/money/${item.bookingId}`}>
              <p>{item.title} · {item.householdLabel} · {formatMinor(item.currency, item.amountMinor)}</p>
              <p className="muted">{item.date ? formatLongDate(item.date) : "Date to confirm"}</p>
            </Link>
          ))}
        </section>
      ) : null}

      {place && upcomingStay && phase !== "after" ? (
        <section>
          <h3 className="section-title">{stay ? "Tonight" : "Next hotel"}</h3>
          <p>{place.name}</p>
          {place.address ? <p className="muted">{place.address}</p> : null}
          <PlaceActions place={place} />
        </section>
      ) : null}
      {action.timingConfidence ? <p className="muted">{humanStatus(action.timingConfidence)}</p> : null}
    </main>
  );
}
